import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {COPILOT_JETBRAINS_SLUG, COPILOT_VSCODE_SLUG} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import type {Persister} from '@github-ui/react-query-persister/types'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {userLevelAgentsMonolithPath} from '../utils/api-url-helpers'

const FilterableAgentsValidator = Compile(
  Type.Array(
    Type.Object(
      // The API sends numeric ids even though `AgentInfo` types them as strings.
      {id: Type.Optional(Type.Union([Type.String(), Type.Number()])), group: Type.Optional(Type.String())},
      {additionalProperties: true},
    ),
  ),
)

/** IndexedDB persister for the filterable agents list, revalidated in the background once restored data is stale. */
export function createFilterableAgentsPersister({
  sendAnalyticsEvent,
  enabled = true,
  partition,
}: {
  sendAnalyticsEvent: SendAnalyticsEventFunction
  enabled?: boolean
  /** Keeps entries saved in different contexts, such as under different SSO access, apart. */
  partition?: string
}): Persister {
  return createPersister({
    validator: FilterableAgentsValidator,
    sendAnalyticsEvent,
    prefix:
      partition === undefined ? 'agent-sessions-filterable-agents' : `agent-sessions-filterable-agents-${partition}`,
    maxAgeMs: 1000 * 60 * 60 * 24,
    alwaysRefetch: false,
    enabled,
  })
}

const BUILTIN_IDE_AGENT_FALLBACKS: AgentInfo[] = [
  {
    id: '797352',
    slug: COPILOT_VSCODE_SLUG,
    display_name: 'Copilot in VS Code',
    avatar_url: '',
    doc_url: '',
    group: 'vscode',
  },
  {
    id: '3135702',
    slug: COPILOT_JETBRAINS_SLUG,
    display_name: 'Copilot in JetBrains',
    avatar_url: '',
    doc_url: '',
    group: 'jetbrains',
  },
]

export function withBuiltinIdeAgentFallbacks(agents: readonly AgentInfo[]): AgentInfo[] {
  let result = [...agents]
  for (const fallback of BUILTIN_IDE_AGENT_FALLBACKS) {
    if (!result.some(agent => agent.group === fallback.group)) {
      result = [...result, fallback]
    }
  }
  return result
}

export function filterableAgentsQueryOptions({
  preserveErrors = false,
  persister,
}: {preserveErrors?: boolean; persister?: Persister} = {}) {
  return {
    queryKey: preserveErrors ? (['filterable-agents', {preserveErrors}] as const) : (['filterable-agents'] as const),
    queryFn: async (): Promise<AgentInfo[]> => {
      const response = await verifiedFetchJSON(userLevelAgentsMonolithPath())
      if (!response.ok) {
        if (preserveErrors || response.status >= 500 || response.status === 429) {
          throw new Error(`Failed to load filterable agents: ${response.status}`)
        }

        return []
      }

      const data: unknown = await response.json()
      const agents = (data as {agents?: unknown} | null)?.agents
      return Array.isArray(agents) ? (agents as AgentInfo[]) : []
    },
    staleTime: 5 * 60 * 1000,
    retry: preserveErrors ? false : 3,
    ...(persister ? {persister} : {}),
  }
}

export function useFilterableAgents({
  enabled = true,
  preserveErrors = false,
  persister,
}: {enabled?: boolean; preserveErrors?: boolean; persister?: Persister} = {}) {
  return useQuery<AgentInfo[]>({...filterableAgentsQueryOptions({preserveErrors, persister}), enabled})
}

/** Numeric IDs of the built-in cloud agents from a filterable-agents list. */
export function resolveCloudAgentIds(agents: AgentInfo[] | undefined): number[] {
  return (agents ?? []).flatMap(agent => (agent.group === 'cloud' && agent.id != null ? [Number(agent.id)] : []))
}
